/**
 * Sector operations — Outside Approvals Client Service (R39 A10.a, SO-P68).
 * Manages outside approval links, subject subscriptions, and callable wrappers.
 */

import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import { getApprovalsFunctions } from './approvalsApp'
import type {
  OutsideApproval,
  OutsideApprover,
  OutsideApprovalSettings,
  OutsideApprovalSubjectType,
  OutsideApprovalStatus,
  OutsideApprovalMode,
  OpsSector,
  CreateOutsideApprovalInput,
  ReissueApproverLinkInput,
  RevokeOutsideApprovalInput,
  SetApprovalSettingsInput,
  GetApprovalLinkInfoInput,
  PrepareApprovalOtpInput,
  OpenApprovalInput,
  DecideApprovalInput,
  CommentApprovalInput,
} from '../types/ops'

// ---------------------------------------------------------------------------
// Callables
// ---------------------------------------------------------------------------

export async function createOutsideApproval(
  input: CreateOutsideApprovalInput,
): Promise<{ approval: OutsideApproval; links: Array<{ approverId: string; phoneMasked: string; token: string }> }> {
  return callable<
    CreateOutsideApprovalInput,
    { approval: OutsideApproval; links: Array<{ approverId: string; phoneMasked: string; token: string }> }
  >('createOutsideApproval', input)
}

export async function reissueApproverLink(
  input: ReissueApproverLinkInput,
): Promise<{ approvalId: string; approverId: string; token: string }> {
  return callable<ReissueApproverLinkInput, { approvalId: string; approverId: string; token: string }>(
    'reissueApproverLink',
    input,
  )
}

export async function revokeOutsideApproval(
  input: RevokeOutsideApprovalInput,
): Promise<{ success: boolean; approvalId: string; status: OutsideApprovalStatus }> {
  return callable<RevokeOutsideApprovalInput, { success: boolean; approvalId: string; status: OutsideApprovalStatus }>(
    'revokeOutsideApproval',
    input,
  )
}

export async function setApprovalSettings(
  input: SetApprovalSettingsInput,
): Promise<{ success: boolean; workspaceId: string; adminOnlyKinds: string[] }> {
  return callable<SetApprovalSettingsInput, { success: boolean; workspaceId: string; adminOnlyKinds: string[] }>(
    'setApprovalSettings',
    input,
  )
}

export async function getApprovalLinkInfo(
  input: GetApprovalLinkInfoInput,
): Promise<{
  workspaceName: string
  title: string
  senderName: string
  phoneMasked: string
  expiresAt: number
  status: OutsideApprovalStatus
  approverStatus: string
  disclaimer: string
}> {
  return callable<
    GetApprovalLinkInfoInput,
    {
      workspaceName: string
      title: string
      senderName: string
      phoneMasked: string
      expiresAt: number
      status: OutsideApprovalStatus
      approverStatus: string
      disclaimer: string
    }
  >('getApprovalLinkInfo', input, { functions: getApprovalsFunctions() })
}

export async function prepareApprovalOtp(
  input: PrepareApprovalOtpInput,
): Promise<{ success: boolean; attemptsRemaining: number }> {
  return callable<PrepareApprovalOtpInput, { success: boolean; attemptsRemaining: number }>(
    'prepareApprovalOtp',
    input,
    { functions: getApprovalsFunctions() },
  )
}

export async function openApproval(
  input: OpenApprovalInput,
): Promise<{
  approval: {
    id: string
    workspaceId: string
    sector: OpsSector
    subjectType: OutsideApprovalSubjectType
    subjectId: string
    kindKey: string
    title: string
    snapshot: Record<string, unknown>
    snapshotHash: string
    subjectVersion: number
    mode: OutsideApprovalMode
    message: string
    expiresAt: number
    status: OutsideApprovalStatus
    disclaimer: string
  }
  approver: {
    approverId: string
    name: string
    phoneMasked: string
    status: string
  }
}> {
  return callable<
    OpenApprovalInput,
    {
      approval: {
        id: string
        workspaceId: string
        sector: OpsSector
        subjectType: OutsideApprovalSubjectType
        subjectId: string
        kindKey: string
        title: string
        snapshot: Record<string, unknown>
        snapshotHash: string
        subjectVersion: number
        mode: OutsideApprovalMode
        message: string
        expiresAt: number
        status: OutsideApprovalStatus
        disclaimer: string
      }
      approver: {
        approverId: string
        name: string
        phoneMasked: string
        status: string
      }
    }
  >('openApproval', input, { functions: getApprovalsFunctions() })
}

export async function decideApproval(
  input: DecideApprovalInput,
): Promise<{
  approval: OutsideApproval
  approver: OutsideApprover
}> {
  return callable<
    DecideApprovalInput,
    {
      approval: OutsideApproval
      approver: OutsideApprover
    }
  >('decideApproval', input, { functions: getApprovalsFunctions() })
}

export async function commentApproval(
  input: CommentApprovalInput,
): Promise<{
  success: boolean
  approvalId: string
  approverId: string
  commentsCount: number
}> {
  return callable<
    CommentApprovalInput,
    {
      success: boolean
      approvalId: string
      approverId: string
      commentsCount: number
    }
  >('commentApproval', input, { functions: getApprovalsFunctions() })
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

export function subscribeApprovalSettings(
  workspaceId: string,
  onUpdate: (settings: OutsideApprovalSettings | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  try {
    const docRef = doc(firestoreDb, 'ops_approval_settings', workspaceId)
    return onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) {
          onUpdate(null)
        } else {
          onUpdate(snap.data() as OutsideApprovalSettings)
        }
      },
      (err) => {
        onError?.(err)
      },
    )
  } catch (err) {
    onError?.(err as Error)
    return () => {}
  }
}

export function subscribeApprovalsForSubject(
  workspaceId: string,
  subjectType: OutsideApprovalSubjectType,
  subjectId: string,
  onUpdate: (approvals: OutsideApproval[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  try {
    const coll = collection(firestoreDb, 'ops_approvals')
    const q = query(
      coll,
      where('workspaceId', '==', workspaceId),
      where('subjectType', '==', subjectType),
      where('subjectId', '==', subjectId),
      orderBy('createdAt', 'desc'),
    )

    return onSnapshot(
      q,
      (snap) => {
        const approvals = snap.docs.map((d) => ({
          ...(d.data() as OutsideApproval),
          id: d.id,
        }))
        onUpdate(approvals)
      },
      (err) => {
        onError?.(err)
      },
    )
  } catch (err) {
    onError?.(err as Error)
    return () => {}
  }
}
